import React from 'react';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Info,
  Link2,
  MessageCircle,
  Power,
  RefreshCcw,
  RotateCcw,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { ConnectionDeleteButton, ConnectionDetail, ConnectionIdentity, ConnectionLink } from './WhatsAppConnections.tsx';
import WhatsAppIcon from '../icons/WhatsAppIcon.tsx';
import type { OfficialWhatsAppConfig } from '../../services/api.ts';
import type { MetaEmbeddedSignupPhase } from '../../hooks/useWhatsAppEmbeddedSignup.ts';

export type WhatsAppProvider = 'zapi' | 'waha' | null;
export type WahaConnectionMode = 'qr' | 'pairing';

export interface WhatsAppStatusData {
  connected: boolean;
  state: string;
  [key: string]: any;
}

export interface WhatsAppDeviceData {
  id: string;
  name: string;
  phone: string;
  imgUrl: string;
  isBusiness: boolean;
  device: {
    sessionName: string;
    device_model: string;
  };
  [key: string]: any;
}

interface ThemeClasses {
  panel: string;
  surface: string;
  input: string;
  title: string;
  body: string;
  muted: string;
  border: string;
  primaryButton: string;
  secondaryButton: string;
  warningButton: string;
  dangerButton: string;
  softBlue: string;
  softGreen: string;
  softAmber: string;
  softRed: string;
  neutralBadge: string;
}

const getTheme = (isDark: boolean): ThemeClasses => ({
  panel: isDark ? 'border-[#27364D] bg-[#0F1D31] text-[#F8FAFC]' : 'border-[#E4E8EF] bg-white text-[#121B29]',
  surface: isDark ? 'border-[#27364D] bg-[#14243A]' : 'border-[#E4E8EF] bg-[#F5F8FF]',
  input: isDark
    ? 'border border-[#27364D] bg-[#14243A] text-[#F8FAFC] placeholder-[#98A2B3]'
    : 'border border-gray-300 bg-white text-gray-800 placeholder-gray-400',
  title: isDark ? 'text-[#F8FAFC]' : 'text-[#121B29]',
  body: isDark ? 'text-[#D0D5DD]' : 'text-[#344054]',
  muted: isDark ? 'text-[#98A2B3]' : 'text-[#667085]',
  border: isDark ? 'border-[#27364D]' : 'border-[#E4E8EF]',
  primaryButton: 'bg-sminy-primary text-white hover:bg-sminy-primary/90 focus:ring-sminy-primary/30',
  secondaryButton: isDark
    ? 'border-[#27364D] bg-[#14243A] text-[#D0D5DD] hover:bg-[#172A43]'
    : 'border-[#E4E8EF] bg-[#F5F8FF] text-[#344054] hover:bg-[#E4E8EF]',
  warningButton: 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500/30',
  dangerButton: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600/30',
  softBlue: isDark ? 'border-sminy-primary/30 bg-sminy-primary/10 text-sminy-primary' : 'border-sminy-primary/20 bg-sminy-primary/5 text-sminy-primary-hover',
  softGreen: isDark ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200' : 'border-emerald-100 bg-emerald-50 text-emerald-800',
  softAmber: isDark ? 'border-amber-500/25 bg-amber-500/10 text-amber-200' : 'border-amber-100 bg-amber-50 text-amber-800',
  softRed: isDark ? 'border-red-500/25 bg-red-500/10 text-red-200' : 'border-red-100 bg-red-50 text-red-700',
  neutralBadge: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700',
});

const getFlowTheme = (isDark: boolean): ThemeClasses => ({
  ...getTheme(isDark),
  body: isDark ? 'text-[#A6B2C3]' : 'text-[#344054]',
  muted: isDark ? 'text-[#7E8CA0]' : 'text-[#667085]',
  input: isDark ? 'border border-[#27364D] bg-[#0F1D31] text-[#F8FAFC]' : 'border border-[#E4E8EF] bg-white text-[#121B29]',
  secondaryButton: isDark ? 'border-[#27364D] bg-[#0F1D31] text-[#A6B2C3] hover:bg-[#14243A]' : 'border-[#E4E8EF] bg-white text-[#344054] hover:bg-gray-50',
  dangerButton: 'bg-[#B42318] text-white hover:bg-[#912018] focus:ring-red-600/30',
});

const qualityLabel = (quality?: string | null) => {
  if (quality === 'GREEN') return 'Otima';
  if (quality === 'YELLOW') return 'Media';
  if (quality === 'RED') return 'Baixa';
  return quality || 'N/A';
};

const iconTileClass = (isDark: boolean, tone: 'blue' | 'green' | 'amber' | 'red' | 'slate' = 'blue') => {
  if (tone === 'green') return isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700';
  if (tone === 'amber') return isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700';
  if (tone === 'red') return isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700';
  if (tone === 'slate') return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
  return isDark ? 'bg-sminy-primary/10 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary';
};

const buttonBaseClass = 'min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60';

interface StatusBadgeProps {
  isDark: boolean;
  tone: 'blue' | 'green' | 'amber' | 'red' | 'slate';
  children: React.ReactNode;
  withDot?: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ isDark, tone, children, withDot = false }) => {
  const theme = getTheme(isDark);
  const toneClass =
    tone === 'blue' ? theme.softBlue
    : tone === 'green' ? theme.softGreen
    : tone === 'amber' ? theme.softAmber
    : tone === 'red' ? theme.softRed
    : theme.neutralBadge;
  const dotClass =
    tone === 'blue' ? 'bg-sminy-primary'
    : tone === 'green' ? 'bg-emerald-500'
    : tone === 'amber' ? 'bg-amber-500'
    : tone === 'red' ? 'bg-red-500'
    : 'bg-gray-400';

  return (
    <span className={`inline-flex min-h-[28px] items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${toneClass}`}>
      {withDot && <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />}
      {children}
    </span>
  );
};

interface WhatsAppConnectionHeaderProps {
  isDark: boolean;
  provider: WhatsAppProvider;
  statusData: WhatsAppStatusData | null;
  officialConnected?: boolean;
}

export const WhatsAppConnectionHeader: React.FC<WhatsAppConnectionHeaderProps> = ({
  isDark,
  provider,
  statusData,
  officialConnected = false,
}) => {
  const theme = getTheme(isDark);
  const deviceConnected = Boolean(statusData?.connected);
  const connectedCount = Number(deviceConnected) + Number(officialConnected);
  const statusLabel = connectedCount === 2
    ? '2 de 2 conectados'
    : connectedCount === 1
      ? '1 de 2 conectado'
      : provider
        ? 'Conexão em andamento'
        : 'Nenhum conectado';
  const statusTone = connectedCount > 0 ? 'green' : provider ? 'amber' : 'slate';

  return (
    <header className={`rounded-2xl border px-4 py-4 sm:px-5 ${
      isDark ? 'border-gray-700 bg-gray-800/95 text-gray-100 shadow-black/20' : 'border-gray-200 bg-white/95 text-gray-800'
    }`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${iconTileClass(isDark, connectedCount > 0 ? 'green' : 'blue')}`}>
            <WhatsAppIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className={`truncate text-lg font-semibold tracking-tight ${theme.title}`}>WhatsApp da clinica</h1>
            <p className={`truncate text-xs ${theme.muted}`}>
              Gerencie o dispositivo e a API Oficial de forma independente.
            </p>
          </div>
        </div>

        <StatusBadge isDark={isDark} tone={statusTone} withDot>
          {statusLabel}
        </StatusBadge>
      </div>
    </header>
  );
};

interface WhatsAppAlertProps {
  type: 'success' | 'error' | 'info';
  text: string;
  isDark: boolean;
}

export const WhatsAppAlert: React.FC<WhatsAppAlertProps> = ({ type, text, isDark }) => {
  const Icon = type === 'success' ? CheckCircle2 : type === 'info' ? Info : AlertCircle;
  const classes = type === 'success'
    ? isDark ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-200' : 'border-emerald-100 bg-emerald-50 text-emerald-800'
    : type === 'info'
      ? isDark ? 'border-sminy-primary/25 bg-sminy-primary/10 text-blue-100' : 'border-sminy-primary/20 bg-sminy-primary/5 text-sminy-primary-hover'
      : isDark ? 'border-red-500/20 bg-red-500/10 text-red-200' : 'border-red-100 bg-red-50 text-red-800';
  const iconClass = type === 'success'
    ? isDark ? 'text-emerald-300' : 'text-emerald-600'
    : type === 'info'
      ? 'text-sminy-primary'
      : isDark ? 'text-red-300' : 'text-red-600';

  return (
    <div
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      className={`flex items-start rounded-2xl border p-4 ${classes}`}
    >
      <Icon className={`mr-3 mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} />
      <span className="text-sm font-medium leading-5">{text}</span>
    </div>
  );
};

interface WhatsAppActivationCardProps {
  isDark: boolean;
  isCollapsed: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onSubmit: (event: React.FormEvent) => void;
}

export const WhatsAppActivationCard: React.FC<WhatsAppActivationCardProps> = ({
  isDark,
  isLoading,
  onSubmit,
}) => {
  const theme = getTheme(isDark);

  return (
    <section className={`rounded-2xl border p-5 transition-colors ${theme.panel}`}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${iconTileClass(isDark, 'blue')}`}>
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className={`text-lg font-semibold tracking-tight ${theme.title}`}>WhatsApp por dispositivo</h2>
            <p className={`mt-1 text-sm ${theme.muted}`}>Inicie a conexao e escolha QR Code ou codigo por telefone.</p>
          </div>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`${buttonBaseClass} w-full px-6 py-3 font-semibold sm:w-auto ${theme.primaryButton}`}
        >
          <MessageCircle className="h-4 w-4" />
          {isLoading ? 'Configurando...' : 'Conectar'}
        </button>
      </form>
    </section>
  );
};

interface WhatsAppPairingPanelProps {
  isDark: boolean;
  provider: Exclude<WhatsAppProvider, null>;
  mode: WahaConnectionMode;
  qrcodeBase64: string;
  qrAttempts: number;
  pairingPhone: string;
  pairingCode: string;
  isPairingLoading: boolean;
  onSelectQr: () => void;
  onSelectPairing: () => void;
  onPairingPhoneChange: (value: string) => void;
  onRequestPairingCode: (event: React.FormEvent) => void;
  onManualRetry: () => void;
}

interface PairingMethodTabsProps {
  isDark: boolean;
  mode: WahaConnectionMode;
  onSelectQr: () => void;
  onSelectPairing: () => void;
}

const PairingMethodTabs: React.FC<PairingMethodTabsProps> = ({ isDark, mode, onSelectQr, onSelectPairing }) => (
  <div className={`mt-8 flex gap-8 border-b ${getFlowTheme(isDark).border}`}>
    {[{ mode: 'qr', label: 'QR Code', onClick: onSelectQr }, { mode: 'pairing', label: 'Por telefone', onClick: onSelectPairing }].map(tab => (
      <button key={tab.mode} type="button" onClick={tab.onClick} aria-pressed={mode === tab.mode}
        className={`min-h-[44px] border-b-2 px-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#73ACFF] ${mode === tab.mode ? `border-sminy-primary ${isDark ? 'text-[#66A3FF]' : 'text-sminy-primary'}` : `border-transparent ${getFlowTheme(isDark).muted}`}`}>{tab.label}</button>
    ))}
  </div>
);

interface QRCodeFrameProps {
  isDark: boolean;
  qrcodeBase64: string;
  isWaha: boolean;
  qrAttempts: number;
  onManualRetry: () => void;
}

const QRCodeFrame: React.FC<QRCodeFrameProps> = ({ isDark, qrcodeBase64, isWaha, qrAttempts, onManualRetry }) => {
  const theme = getTheme(isDark);
  const spinnerClass = isWaha ? 'border-emerald-600' : 'border-sminy-primary';

  return (
    <div className="flex flex-col items-center">
      <div className={`relative flex aspect-square w-full max-w-[256px] items-center justify-center rounded-lg border p-3 ${theme.surface}`}>
        {qrcodeBase64 ? (
          <img src={qrcodeBase64} alt="QR Code" className="h-auto w-56 max-w-full rounded bg-white p-2" />
        ) : (
          <div className={`h-8 w-8 animate-spin rounded-full border-4 ${spinnerClass} border-t-transparent`} />
        )}
      </div>

      <p className={`mt-4 max-w-md text-center text-sm ${theme.body}`}>
        {qrcodeBase64
          ? isWaha
            ? 'Abra o WhatsApp > Dispositivos conectados > Conectar dispositivo.'
            : 'Abra o WhatsApp e escaneie o código acima.'
          : isWaha
            ? `Aguardando QR Code do WhatsApp... tentativa ${qrAttempts}/10.`
            : 'Gerando QR Code...'}
      </p>

      {isWaha && qrAttempts >= 10 && !qrcodeBase64 && (
        <button
          type="button"
          onClick={onManualRetry}
          className={`${buttonBaseClass} mt-5 px-5 py-3 font-semibold ${theme.primaryButton}`}
        >
          <RefreshCcw className="h-4 w-4" />
          Tentar novamente
        </button>
      )}
    </div>
  );
};

interface ConnectionGuideProps {
  isDark: boolean;
  mode: WahaConnectionMode;
  isWaha: boolean;
}

const ConnectionGuide: React.FC<ConnectionGuideProps> = ({ isDark, mode, isWaha }) => {
  const theme = getFlowTheme(isDark);
  const steps = mode === 'pairing' && isWaha
    ? ['Abra Dispositivos conectados no WhatsApp.', 'Toque em Conectar dispositivo.', 'Escolha Conectar com número de telefone e digite o código.']
    : ['Abra Dispositivos conectados no WhatsApp.', 'Toque em Conectar dispositivo e escaneie o QR Code.', 'Aguarde a confirmação da conexão na Sminy.'];
  return <div className={`mt-9 border-t pt-6 ${theme.border}`}>
    <h3 className={`text-xs font-medium ${theme.body}`}>{mode === 'pairing' && isWaha ? 'Depois de gerar o código' : 'No celular'}</h3>
    <ol className="mt-6 space-y-5">{steps.map((step, i) => <li key={step} className={`flex gap-4 text-xs leading-relaxed ${theme.body}`}><span className={`shrink-0 ${theme.muted}`}>{String(i + 1).padStart(2, '0')}</span>{step}</li>)}</ol>
  </div>;
};

export const WhatsAppPairingPanel: React.FC<WhatsAppPairingPanelProps> = ({
  isDark, provider, mode, qrcodeBase64, qrAttempts, pairingPhone, pairingCode, isPairingLoading,
  onSelectQr, onSelectPairing, onPairingPhoneChange, onRequestPairingCode, onManualRetry,
}) => {
  const theme = getFlowTheme(isDark);
  const isWaha = provider === 'waha';
  return (
    <section>
      <h2 className={`text-xl font-semibold tracking-tight ${theme.title}`}>Conectar dispositivo</h2>
      <p className={`mt-3 text-[13px] ${theme.body}`}>Tenha o celular da clínica em mãos.</p>
      {isWaha && <PairingMethodTabs isDark={isDark} mode={mode} onSelectQr={onSelectQr} onSelectPairing={onSelectPairing} />}
      {mode === 'qr' || !isWaha ? <div className="mt-8 max-w-[390px]">
        <QRCodeFrame isDark={isDark} qrcodeBase64={qrcodeBase64} isWaha={isWaha} qrAttempts={qrAttempts} onManualRetry={onManualRetry} />
        <div className="mt-3 text-center"><ConnectionLink dark={isDark} onClick={onManualRetry}><RefreshCcw size={14} aria-hidden="true" />Atualizar</ConnectionLink></div>
      </div> : <form onSubmit={onRequestPairingCode} className="mt-8 flex w-full max-w-[390px] flex-col items-start gap-5">
        <div className="w-full">
          <label htmlFor="whatsapp-pairing-phone" className={`mb-3 block text-xs font-medium ${theme.body}`}>Número do WhatsApp</label>
          <input id="whatsapp-pairing-phone" type="tel" inputMode="tel" autoComplete="tel" value={pairingPhone}
            onChange={event => onPairingPhoneChange(event.target.value)} aria-describedby="whatsapp-phone-hint"
            className={`min-h-[44px] w-full rounded-lg px-3 text-[13px] outline-none focus:ring-2 focus:ring-sminy-primary ${theme.input}`}
            placeholder="+55 (11) 99999-9999" disabled={isPairingLoading} />
          <p id="whatsapp-phone-hint" className={`mt-2 text-xs ${theme.muted}`}>Inclua o código do país e o DDD.</p>
        </div>
        <button type="submit" disabled={isPairingLoading} className={`${buttonBaseClass} w-full !text-xs sm:w-auto ${theme.primaryButton}`}>{isPairingLoading ? 'Gerando código...' : 'Gerar código'}</button>
        {pairingCode && <div role="status" className={`w-full border-y py-5 ${theme.border}`}>
          <p className={`text-xs ${theme.muted}`}>Código de pareamento</p>
          <p className={`mt-3 break-all font-mono text-2xl font-semibold tracking-widest ${theme.title}`}>{pairingCode}</p>
        </div>}
      </form>}
      <ConnectionGuide isDark={isDark} mode={mode} isWaha={isWaha} />
    </section>
  );
};

interface WhatsAppOfficialAccountCardProps {
  isDark: boolean;
  config: OfficialWhatsAppConfig | null;
  isLoading?: boolean;
  availability?: 'loading' | 'ready' | 'error';
  availabilityMessage?: string;
  signupPhase?: MetaEmbeddedSignupPhase;
  onConnect: () => void;
  onRefresh: () => void;
  onRetryPreparation?: () => void;
  onDisconnect: () => void;
}

export const WhatsAppOfficialAccountCard: React.FC<WhatsAppOfficialAccountCardProps> = ({
  isDark,
  config,
  isLoading,
  availability = 'ready',
  availabilityMessage,
  signupPhase = 'ready',
  onConnect,
  onRefresh,
  onRetryPreparation,
  onDisconnect,
}) => {
  const theme = getFlowTheme(isDark);
  const connected = Boolean(config?.has_official);
  const legacy = config?.status === 'legacy_connected';
  const provisioning = config?.status === 'provisioning';
  const disconnecting = config?.status === 'disconnecting';
  const disconnectError = config?.status === 'disconnect_error';
  const statusTone: 'green' | 'amber' | 'red' | 'slate' = connected
    ? 'green'
    : config?.status === 'error' || config?.status === 'action_required' || disconnectError
      ? 'red'
      : provisioning || disconnecting
        ? 'amber'
        : 'slate';
  const statusLabel = legacy
    ? 'Conexão legada'
    : connected
      ? 'Conectado'
      : config?.status === 'provisioning'
        ? 'Ativação em andamento'
        : disconnecting
          ? 'Desconectando'
          : disconnectError
            ? 'Falha ao desconectar'
            : config?.status === 'action_required'
              ? 'Ação necessária'
              : config?.status === 'error'
                ? 'Erro'
                : 'Não conectado';
  const phaseLabel: Partial<Record<MetaEmbeddedSignupPhase, string>> = {
    preparing_sdk: 'Preparando conexão segura com a Meta...',
    opening_meta: 'Abrindo a autorização da Meta...',
    awaiting_meta: 'Aguardando a conclusão na Meta...',
    creating_session: 'Preparando a conexão da clínica...',
    completing: 'Finalizando a conexão oficial...',
  };
  const connectLabel: Partial<Record<MetaEmbeddedSignupPhase, string>> = {
    preparing_sdk: 'Preparando Meta...',
    opening_meta: 'Abrindo Meta...',
    awaiting_meta: 'Aguardando Meta...',
    creating_session: 'Preparando conexão...',
    completing: 'Finalizando conexão...',
  };
  if (connected && !provisioning && !disconnecting && !disconnectError) {
    return <section>
      <h2 className={`text-xl font-semibold ${theme.title}`}>API oficial</h2>
      <p className={`mt-3 text-[13px] ${theme.body}`}>Informações e ações desta conexão.</p>
      <div className="mt-9"><ConnectionIdentity dark={isDark} row={{ kind: 'official', name: 'API oficial da Meta', profileName: config?.verified_name || undefined, imageUrl: config?.profile_picture_url || undefined, phone: config?.display_phone_number || 'Número indisponível', status: statusLabel, tone: 'success', reconnect: false }} /></div>
      <dl className={`mt-8 border-t ${theme.border}`}>
        <ConnectionDetail dark={isDark} label="Número">{config?.display_phone_number || 'Número indisponível'}</ConnectionDetail>
        <ConnectionDetail dark={isDark} label="Status"><span className="inline-flex items-center gap-2"><span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${availability === 'error' ? 'bg-red-400' : legacy ? 'bg-slate-400' : 'bg-emerald-500'}`} />{availability === 'error' ? 'Status indisponível' : statusLabel}</span></ConnectionDetail>
        <ConnectionDetail dark={isDark} label="Qualidade do número">{qualityLabel(config?.quality_rating)}</ConnectionDetail>
      </dl>
      {availability === 'error' && <p role="alert" className={`mt-6 text-xs ${theme.muted}`}>{availabilityMessage || 'Não foi possível verificar a conexão oficial agora.'}</p>}
      {!legacy && <div className="mt-2 flex justify-end"><ConnectionLink dark={isDark} disabled={isLoading} onClick={availability === 'error' ? onRetryPreparation : onRefresh}><RefreshCcw size={14} aria-hidden="true" />{availability === 'error' ? 'Tentar preparar novamente' : 'Verificar conexão'}</ConnectionLink></div>}
      {!legacy && availability !== 'error' && <div className={`mt-12 border-t pt-6 ${theme.border}`}>
        <h3 className="text-sm font-medium">Excluir esta conexão</h3>
        <p className={`mt-3 text-xs ${theme.muted}`}>O WhatsApp por dispositivo continuará conectado.</p>
        <div className="mt-4"><ConnectionDeleteButton dark={isDark} disabled={isLoading} onClick={onDisconnect} /></div>
      </div>}
    </section>;
  }

  const activePhaseLabel = phaseLabel[signupPhase];
  const canStartSignup = !connected
    && !legacy
    && !provisioning
    && !disconnecting
    && !disconnectError
    && availability === 'ready';

  return (
    <section>
      <h2 className={`text-xl font-semibold ${theme.title}`}>{provisioning ? 'Ativando a API oficial' : 'API oficial da Meta'}</h2>
      <p className={`mt-3 text-[13px] leading-relaxed ${theme.body}`}>{provisioning ? 'Conta autorizada; aguardando a ativação do número.' : disconnecting ? 'Finalizando a desconexão segura da Meta.' : disconnectError ? 'A desconexão precisa ser tentada novamente.' : 'Autorize com sua conta Meta para conectar o número da clínica.'}</p>
      <div className={`mt-9 border-y py-5 ${theme.border}`}>
        {provisioning && <div className={`mb-6 flex items-start gap-3 ${theme.body}`}><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-500" aria-hidden="true" /><div><h3 className="text-sm font-medium">Autorização recebida</h3><p className={`mt-2 text-xs ${theme.muted}`}>Conta Meta autorizada com sucesso.</p></div></div>}
        <div className="flex items-center gap-3"><span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusTone === 'red' ? 'bg-red-400' : statusTone === 'green' ? 'bg-emerald-500' : 'bg-slate-400'}`} /><p className="text-sm font-medium">{statusLabel}</p></div>
        {provisioning && <p className={`ml-5 mt-2 text-xs ${theme.muted}`}>Você pode sair desta tela e acompanhar depois.</p>}
        {config?.last_error && !provisioning && <p role="alert" className={`mt-4 text-xs ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>{config.last_error}</p>}
      </div>
      {(availability !== 'ready' || activePhaseLabel) && !connected && !legacy && <p role={availability === 'error' ? 'alert' : 'status'} aria-live={availability === 'error' ? 'assertive' : 'polite'} className={`mt-5 text-xs leading-relaxed ${theme.body}`}>
        {availability === 'error' ? availabilityMessage || 'A integração com a Meta não está disponível agora.' : activePhaseLabel || 'Preparando conexão segura com a Meta...'}
      </p>}
        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap">
          {canStartSignup && (
            <button
              type="button"
              onClick={onConnect}
              disabled={isLoading}
              aria-busy={Boolean(isLoading)}
              className={`${buttonBaseClass} w-full !text-xs sm:w-auto ${theme.primaryButton}`}
            >
              <ShieldCheck className="h-4 w-4" />
              {isLoading ? connectLabel[signupPhase] || 'Processando...' : 'Conectar com a Meta'}
            </button>
          )}
          {!connected && !legacy && !provisioning && availability === 'loading' && (
            <button
              type="button"
              disabled
              aria-busy="true"
              className={`${buttonBaseClass} w-full !text-xs sm:w-auto ${theme.primaryButton}`}
            >
              <RefreshCcw className="h-4 w-4 animate-spin" />
              Preparando Meta...
            </button>
          )}
          {!connected && !legacy && !provisioning && availability === 'error' && onRetryPreparation && (
            <button
              type="button"
              onClick={onRetryPreparation}
              className={`${buttonBaseClass} w-full !text-xs sm:w-auto border ${theme.secondaryButton}`}
            >
              <RefreshCcw className="h-4 w-4" />
              Tentar preparar novamente
            </button>
          )}
          {provisioning && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className={`${buttonBaseClass} w-full !text-xs sm:w-auto border ${theme.secondaryButton}`}
            >
              <RefreshCcw className="h-4 w-4" />
              Verificar ativação
            </button>
          )}
          {connected && !legacy && (
            <button type="button" onClick={onRefresh} disabled={isLoading} className={`${buttonBaseClass} w-full !text-xs sm:w-auto border ${theme.secondaryButton}`}>
              <RefreshCcw className="h-4 w-4" />
              Verificar conexão
            </button>
          )}
          {connected && !legacy && (
            <button type="button" onClick={onDisconnect} disabled={isLoading} className={`${buttonBaseClass} w-full !text-xs sm:w-auto ${theme.warningButton}`}>
              <Power className="h-4 w-4" />
              Desconectar Meta
            </button>
          )}
          {disconnectError && (
            <button type="button" onClick={onDisconnect} disabled={isLoading} className={`${buttonBaseClass} w-full !text-xs sm:w-auto ${theme.warningButton}`}>
              <Power className="h-4 w-4" />
              Tentar desconectar novamente
            </button>
          )}
          {disconnecting && (
            <button type="button" onClick={onDisconnect} disabled={isLoading} className={`${buttonBaseClass} w-full !text-xs sm:w-auto border ${theme.secondaryButton}`}>
              <RefreshCcw className="h-4 w-4" />
              Verificar desconexão
            </button>
          )}
          {legacy && (
            <p className={`sm:col-span-2 text-xs ${theme.muted}`}>
              Esta conexão continua ativa e não será substituída automaticamente pelo OAuth.
            </p>
          )}
        </div>

      {provisioning && <p className={`mt-5 text-xs ${theme.muted}`}>Não é necessário iniciar outra conexão.</p>}
    </section>
  );
};

interface WhatsAppDeviceCardProps {
  isDark: boolean;
  deviceData: WhatsAppDeviceData | null;
  connected?: boolean;
}

export const WhatsAppDeviceCard: React.FC<WhatsAppDeviceCardProps> = ({ isDark, deviceData, connected }) => {
  const theme = getTheme(isDark);

  if (!deviceData || !connected) return null;

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${theme.panel}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-sminy-primary" />
          <h3 className={`text-sm font-semibold ${theme.title}`}>Dispositivo conectado</h3>
        </div>
        <StatusBadge isDark={isDark} tone="green">online</StatusBadge>
      </div>
      <div className="flex items-center gap-4">
        {deviceData.imgUrl ? (
          <img src={deviceData.imgUrl} alt="Perfil" className="h-14 w-14 rounded-2xl object-cover shadow-sm" />
        ) : (
          <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${iconTileClass(isDark, 'green')}`}>
            <Smartphone className="h-6 w-6" />
          </div>
        )}
        <div className="min-w-0">
          <p className={`truncate text-lg font-semibold ${theme.title}`}>{deviceData.name || 'WhatsApp conectado'}</p>
          <p className={`truncate text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{deviceData.phone || '-'}</p>
          <p className={`mt-1 text-sm ${theme.muted}`}>{deviceData.device?.device_model || 'WhatsApp'}</p>
        </div>
      </div>
    </section>
  );
};

interface WhatsAppWebhookCardProps {
  isDark: boolean;
  webhookUrl: string;
}

export const WhatsAppWebhookCard: React.FC<WhatsAppWebhookCardProps> = ({ isDark, webhookUrl }) => {
  const theme = getTheme(isDark);
  if (!webhookUrl) return null;

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${theme.panel}`}>
      <h3 className={`mb-3 flex items-center gap-2 text-sm font-semibold ${theme.title}`}>
        <Link2 className="h-4 w-4 text-sminy-primary" />
        URL do Webhook
      </h3>
      <div className={`rounded-xl border p-3 ${theme.surface}`}>
        <p className={`break-all font-mono text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{webhookUrl}</p>
      </div>
    </section>
  );
};

interface WhatsAppQuickActionsProps {
  isDark: boolean;
  provider: WhatsAppProvider;
  connected?: boolean;
  isLoading: boolean;
  onRefresh: () => void;
  onDisconnect: () => void;
  onReset: () => void;
}

export const WhatsAppQuickActions: React.FC<WhatsAppQuickActionsProps> = ({
  isDark,
  provider,
  connected,
  isLoading,
  onRefresh,
  onDisconnect,
  onReset,
}) => {
  const theme = getTheme(isDark);
  if (!provider || isLoading) return null;

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${theme.panel}`}>
      <div className="mb-4 flex items-center gap-2">
        <Activity className={`h-4 w-4 ${isDark ? 'text-sminy-primary' : 'text-sminy-primary-hover'}`} />
        <h3 className={`text-sm font-semibold ${theme.title}`}>Acoes rapidas</h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
        {!connected && (
          <button
            type="button"
            onClick={onRefresh}
            className={`${buttonBaseClass} w-full px-5 py-3 font-semibold ${theme.primaryButton}`}
          >
            <RefreshCcw className="h-4 w-4" />
            {provider === 'waha' ? 'Atualizar QR Code' : 'Atualizar status'}
          </button>
        )}

        {connected && (
          <button
            type="button"
            onClick={onDisconnect}
            className={`${buttonBaseClass} w-full px-5 py-3 font-semibold ${theme.warningButton}`}
          >
            <Power className="h-4 w-4" />
            Desconectar
          </button>
        )}

        {!connected && (provider === 'zapi' || provider === 'waha') && (
          <button
            type="button"
            onClick={onReset}
            className={`${buttonBaseClass} w-full px-5 py-3 font-semibold ${theme.dangerButton}`}
          >
            <RotateCcw className="h-4 w-4" />
            Resetar
          </button>
        )}
      </div>
    </section>
  );
};

interface WhatsAppOperationalPanelProps {
  isDark: boolean;
  provider: WhatsAppProvider;
  connected?: boolean;
  isLoading: boolean;
  deviceData: WhatsAppDeviceData | null;
  officialConfig: OfficialWhatsAppConfig | null;
  webhookUrl: string;
  onRefresh: () => void;
  onDisconnect: () => void;
  onReset: () => void;
}

export const WhatsAppOperationalPanel: React.FC<WhatsAppOperationalPanelProps> = ({
  isDark,
  provider,
  connected,
  isLoading,
  deviceData,
  officialConfig,
  onRefresh,
  onDisconnect,
  onReset,
}) => {
  const theme = getTheme(isDark);
  if (!provider) return null;

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${theme.panel}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {connected && deviceData?.imgUrl ? (
            <img src={deviceData.imgUrl} alt="Perfil" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          ) : (
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconTileClass(isDark, connected ? 'green' : 'amber')}`}>
              <Smartphone className="h-6 w-6" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className={`truncate text-sm font-semibold ${theme.title}`}>
                {connected
                  ? deviceData?.name || deviceData?.phone || 'Dispositivo conectado'
                  : 'Aguardando conexao do dispositivo'}
              </p>
              <StatusBadge isDark={isDark} tone={connected ? 'green' : 'amber'} withDot>
                {connected ? 'online' : 'pendente'}
              </StatusBadge>
            </div>
            <p className={`mt-1 truncate text-sm ${theme.muted}`}>
              {connected
                ? deviceData?.phone || deviceData?.device?.device_model || 'WhatsApp ativo'
                : 'Escaneie o QR Code ou gere um codigo para concluir.'}
            </p>
            {connected && officialConfig?.has_official && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge isDark={isDark} tone="blue">Meta oficial</StatusBadge>
                <span className={`truncate text-xs ${theme.muted}`}>
                  {officialConfig.verified_name || officialConfig.display_phone_number || qualityLabel(officialConfig.quality_rating)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={`grid w-full shrink-0 gap-2 ${connected ? 'sm:grid-cols-1 lg:w-[220px]' : 'sm:grid-cols-2 lg:w-auto lg:min-w-[360px]'}`}>
          {!connected && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className={`${buttonBaseClass} w-full ${theme.primaryButton}`}
            >
              <RefreshCcw className="h-4 w-4" />
              {provider === 'waha' ? 'Atualizar QR Code' : 'Atualizar status'}
            </button>
          )}

          {connected && (
            <button
              type="button"
              onClick={onDisconnect}
              disabled={isLoading}
              className={`${buttonBaseClass} w-full ${theme.warningButton}`}
            >
              <Power className="h-4 w-4" />
              Desconectar
            </button>
          )}

          {!connected && (
            <button
              type="button"
              onClick={onReset}
              disabled={isLoading}
              className={`${buttonBaseClass} w-full border ${isDark ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10 focus:ring-rose-500/20' : 'border-rose-200 text-rose-700 hover:bg-rose-50 focus:ring-rose-500/20'}`}
            >
              <RotateCcw className="h-4 w-4" />
              Resetar configuracao
            </button>
          )}
        </div>
      </div>

    </section>
  );
};

interface WhatsAppConfirmModalProps {
  isDark: boolean;
  variant: 'disconnect' | 'official-disconnect' | 'reset';
  connectionName?: string;
  connectionPhone?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export const WhatsAppConfirmModal: React.FC<WhatsAppConfirmModalProps> = ({
  isDark,
  variant,
  connectionName,
  connectionPhone,
  onCancel,
  onConfirm,
}) => {
  const theme = getFlowTheme(isDark);
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const isReset = variant === 'reset';
  const isOfficial = variant === 'official-disconnect';
  const titleId = `whatsapp-confirm-title-${variant}`;
  const descriptionId = `whatsapp-confirm-description-${variant}`;
  const title = isReset ? 'Excluir conexão do dispositivo?' : isOfficial ? 'Excluir conexão com a API oficial?' : 'Confirmar desconexao';
  const description = isReset
    ? 'Remove a configuração deste dispositivo da Sminy. Para usá-lo novamente, será necessário conectar. A API oficial não será alterada.'
    : isOfficial
      ? 'A Sminy deixará de usar esta autorização da Meta. O WhatsApp por dispositivo não será afetado.'
      : 'Tem certeza que deseja desconectar? Voce precisara parear o WhatsApp novamente.';
  const confirmClass = theme.dangerButton;

  React.useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    cancelButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
      if (event.key === 'Tab') {
        const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); previousFocus?.focus(); };
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={`max-h-[calc(100dvh-32px)] w-full max-w-md overflow-y-auto rounded-xl border ${theme.panel}`}
      >
        <div className="p-6">
          <h3 id={titleId} className={`text-lg font-semibold ${theme.title}`}>{title}</h3>
          <p id={descriptionId} className={`mt-4 text-[13px] leading-relaxed ${theme.body}`}>{description}</p>
          {(isOfficial || isReset) && <div className={`my-6 border-y py-4 ${theme.border}`}>
            <p className="break-words text-sm font-medium">{connectionName || (isOfficial ? 'API oficial da Meta' : 'WhatsApp por dispositivo')}</p>
            <p className={`mt-2 break-words text-xs ${theme.muted}`}>{connectionPhone || 'Número indisponível'}</p>
          </div>}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              ref={cancelButtonRef}
              onClick={onCancel}
              className={`${buttonBaseClass} border px-4 !text-xs ${theme.secondaryButton}`}
            >
              {isOfficial || isReset ? 'Manter conexão' : 'Cancelar'}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`${buttonBaseClass} px-4 !text-xs font-semibold ${confirmClass}`}
            >
              {isReset || isOfficial ? 'Excluir conexão' : 'Sim, desconectar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface WhatsAppLoadingOverlayProps {
  isDark: boolean;
}

export const WhatsAppLoadingOverlay: React.FC<WhatsAppLoadingOverlayProps> = ({ isDark }) => {
  const theme = getTheme(isDark);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Processando conexão do WhatsApp"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      <div className={`flex items-center rounded-2xl border p-6 shadow-2xl ${theme.panel}`}>
        <div className="mr-4 h-8 w-8 animate-spin rounded-full border-4 border-sminy-primary border-t-transparent" />
        <div>
          <p className={`text-lg font-semibold ${theme.title}`}>Processando...</p>
          <p className={`text-xs ${theme.muted}`}>Atualizando a conexao do WhatsApp</p>
        </div>
      </div>
    </div>
  );
};
